import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { useConfirm, usePrompt } from "../contexts/ConfirmProvider";
import { useSubscription } from "../contexts/SubscriptionProvider";
import { useOrg } from "../contexts/OrgProvider";
import { FRAMEWORKS as ALL_FRAMEWORKS } from "../constants/frameworks";
import { TableSkeleton } from "../components/skeletons";
import { EmptyState } from "../components/EmptyState";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Search, FileText, Folder, Edit2, Trash2, Clock,
  CheckCircle2, AlertCircle, Eye, Save, X, Upload, Download,
  Send, ShieldCheck, Rocket, History, PenLine
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { StatusBadge, TEXT_TONE } from "../components/ui/status-badge";
import { getStatusTone } from "../lib/statusTokens";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from "../components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter
} from "../components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger
} from "../components/ui/alert-dialog";
import { extractError } from "../lib/errors";
import { toast } from "sonner";
import SendForSignatureDialog from "../components/SendForSignatureDialog";
import { PageHeader } from "../components/PageHeader";

// qa BAJA-MEDIA: mapa de ÍCONO por estado documental — ortogonal al color, que
// sigue saliendo de getStatusTone/StatusBadge (ADR-P1-001). No reintroducir un
// mapa de color aquí; mismo patrón que RESULT_CONFIG.icon en AuditChecklistTab.jsx.
const DOC_STATUS_ICON = {
  draft: Clock,
  under_review: AlertCircle,
  approved: CheckCircle2,
  published: CheckCircle2,
};

const Documents = () => {
  const { token, user } = useAuth();
  const { t } = useI18n();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const { hasFeature } = useSubscription();
  const { activeFrameworks } = useOrg();
  const canESign = hasFeature("esignature");
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [signatureDoc, setSignatureDoc] = useState(null);

  const [newDoc, setNewDoc] = useState({
    title: "",
    document_type: "policy",
    framework: "",
    content: "",
    folder: ""
  });

  const documentTypes = [
    { value: "policy", labelKey: "docs.type.policy" },
    { value: "procedure", labelKey: "docs.type.procedure" },
    { value: "plan", labelKey: "docs.type.plan" },
    { value: "register", labelKey: "docs.type.register" },
    { value: "report", labelKey: "docs.type.report" },
    { value: "form", labelKey: "docs.type.form" }
  ];

  const frameworks = ALL_FRAMEWORKS
    .filter((f) => activeFrameworks.includes(f.id))
    .map((f) => ({ value: f.id, label: f.short }));

  const statuses = [
    { value: "draft", labelKey: "docs.status.draft" },
    { value: "under_review", labelKey: "docs.status.under_review" },
    { value: "approved", labelKey: "docs.status.approved" },
    { value: "published", labelKey: "docs.status.published" }
  ];

  useEffect(() => {
    fetchDocuments();
  }, [token]);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error("Failed to fetch documents:", error);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handleAddDocument = async () => {
    if (!newDoc.title) {
      toast.error("Please enter a document title");
      return;
    }

    try {
      await axios.post(`${API}/documents`, newDoc, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Document created successfully");
      setShowAddDoc(false);
      setNewDoc({ title: "", document_type: "policy", framework: "", content: "", folder: "" });
      fetchDocuments();
    } catch (error) {
      toast.error(extractError(error, "Failed to create document"));
    }
  };

  const handleUpdateDocument = async () => {
    if (!editingDoc) return;

    try {
      await axios.put(`${API}/documents/${editingDoc.document_id}`, {
        title: editingDoc.title,
        document_type: editingDoc.document_type,
        framework: editingDoc.framework,
        content: editingDoc.content,
        folder: editingDoc.folder
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Document updated successfully");
      setEditingDoc(null);
      fetchDocuments();
    } catch (error) {
      toast.error(extractError(error, "Failed to update document"));
    }
  };

  const handleUpdateStatus = async (docId, newStatus, comment = "") => {
    try {
      const url = `${API}/documents/${docId}/status?status=${newStatus}${comment ? `&comment=${encodeURIComponent(comment)}` : ""}`;
      await axios.put(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Status updated to ${newStatus.replace("_", " ")}`);
      fetchDocuments();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const submitForReview = async (doc) => {
    // P2 — cancelar el prompt ABORTA el envío (única excepción de comportamiento
    // autorizada por el diseño §2c/§6: hoy window.prompt cancelado igual enviaba "").
    const comment = await prompt({
      title: t("docs.review_comment_title"),
      label: t("docs.review_comment_label"),
      multiline: true,
    });
    if (comment === null) return;
    handleUpdateStatus(doc.document_id, "under_review", comment);
  };

  const approveDoc = async (doc) => {
    // P2 — cancelar el prompt ABORTA la aprobación (mismo cambio de comportamiento que arriba).
    const comment = await prompt({
      title: t("docs.approval_comment_title"),
      label: t("docs.approval_comment_label"),
      multiline: true,
    });
    if (comment === null) return;
    handleUpdateStatus(doc.document_id, "approved", comment);
  };

  const publishDoc = async (doc) => {
    if (await confirm({
      title: t("docs.publish_title"),
      description: `${t("docs.publish_desc_prefix")} "${doc.title}"${t("docs.publish_desc_suffix")}`,
      destructive: true,
    })) {
      handleUpdateStatus(doc.document_id, "published", "Published");
    }
  };

  const reopenDoc = (doc) => {
    handleUpdateStatus(doc.document_id, "draft", "Reopened for edits");
  };

  const downloadPdf = async (doc) => {
    try {
      const res = await axios.get(`${API}/documents/${doc.document_id}/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${doc.title.replace(/[^a-z0-9]/gi, "_").slice(0, 50)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch {
      toast.error("PDF export failed");
    }
  };

  const [historyDoc, setHistoryDoc] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const viewHistory = async (doc) => {
    setHistoryDoc(doc);
    try {
      const res = await axios.get(`${API}/documents/${doc.document_id}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistoryData(res.data);
    } catch {
      toast.error("Failed to load history");
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await axios.delete(`${API}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Document deleted");
      fetchDocuments();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const getFrameworkBadgeClass = (framework) => {
    switch (framework) {
      case "iso27001": return "badge-iso27001";
      case "nist_scf": return "badge-nist";
      case "cis_v8": return "badge-cis";
      case "iso20000": return "badge-iso20000";
      default: return "";
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === "all" || doc.document_type === selectedType;
    const matchesStatus = selectedStatus === "all" || doc.status === selectedStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const documentsByType = documentTypes.map(type => ({
    ...type,
    count: documents.filter(d => d.document_type === type.value).length
  }));

  if (loading) {
    return <TableSkeleton rows={6} columns={5} label="Loading documents" />;
  }

  const ViewingDocIcon = viewingDoc ? DOC_STATUS_ICON[viewingDoc.status] : null;

  return (
    <div className="p-6 space-y-6" data-testid="documents-page">
      <PageHeader
        title={t("docs.title")}
        description={t("docs.subtitle")}
        actions={
          <Button onClick={() => setShowAddDoc(true)} data-testid="add-document-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t("docs.upload")}
          </Button>
        }
      />

      {/* Document Type Summary */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        {documentsByType.map(type => (
          <Card
            key={type.value}
            className={`cursor-pointer transition-all ${selectedType === type.value ? 'ring-2 ring-primary' : 'hover:border-primary/50'}`}
            onClick={() => setSelectedType(selectedType === type.value ? "all" : type.value)}
          >
            <CardContent className="pt-4 pb-4 text-center">
              <FileText className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
              <p className="text-lg font-heading font-bold">{type.count}</p>
              <p className="text-xs text-muted-foreground">{t(type.labelKey)}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t("docs.search_ph")}
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="document-search-input"
          />
        </div>
        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-full md:w-40" data-testid="status-filter">
            <SelectValue placeholder={t("status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("docs.all_statuses")}</SelectItem>
            {statuses.map(s => (
              <SelectItem key={s.value} value={s.value}>{t(s.labelKey)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(selectedType !== "all" || selectedStatus !== "all" || searchQuery) && (
          <Button
            variant="ghost"
            onClick={() => { setSelectedType("all"); setSelectedStatus("all"); setSearchQuery(""); }}
          >
            Clear <X className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>

      {/* Results Count */}
      <p className="text-sm text-muted-foreground">
        {t("docs.showing").replace("{count}", filteredDocuments.length).replace("{total}", documents.length)}
      </p>

      {/* Documents List */}
      <ScrollArea className="h-[calc(100vh-28rem)]">
        <div className="space-y-3 pr-4">
          <AnimatePresence mode="popLayout">
            {filteredDocuments.map((doc, index) => {
              const DocIcon = DOC_STATUS_ICON[doc.status];
              return (
              <motion.div
                key={doc.document_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, delay: index * 0.02 }}
              >
                <Card className="border-border hover:border-primary/50 transition-colors" data-testid={`document-card-${doc.document_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="inline-flex items-center gap-1.5">
                            {DocIcon && <DocIcon className={`w-3.5 h-3.5 ${TEXT_TONE[getStatusTone("doc", doc.status)]}`} />}
                            <StatusBadge kind="doc" value={doc.status} />
                          </span>
                          <Badge variant="secondary" className="capitalize">
                            {doc.document_type}
                          </Badge>
                          {doc.framework && (
                            <Badge variant="outline" className={getFrameworkBadgeClass(doc.framework)}>
                              {frameworks.find(f => f.value === doc.framework)?.label || doc.framework}
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            v{doc.version}
                          </span>
                        </div>
                        <h3 className="font-semibold mb-1">{doc.title}</h3>
                        <p className="text-sm text-muted-foreground">
                          {t("docs.updated")} {new Date(doc.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {/* Workflow action buttons based on status */}
                        {doc.status === "draft" && (
                          <Button variant="ghost" size="icon" onClick={() => submitForReview(doc)}
                            data-testid={`submit-review-${doc.document_id}`} title="Submit for Review">
                            <Send className="w-4 h-4 text-yellow-500" />
                          </Button>
                        )}
                        {doc.status === "under_review" && (
                          <Button variant="ghost" size="icon" onClick={() => approveDoc(doc)}
                            data-testid={`approve-${doc.document_id}`} title="Approve">
                            <ShieldCheck className="w-4 h-4 text-green-500" />
                          </Button>
                        )}
                        {doc.status === "approved" && (
                          <Button variant="ghost" size="icon" onClick={() => publishDoc(doc)}
                            data-testid={`publish-${doc.document_id}`} title="Publish">
                            <Rocket className="w-4 h-4 text-blue-500" />
                          </Button>
                        )}
                        {(doc.status === "approved" || doc.status === "published") && canESign && (
                          <Button variant="ghost" size="icon" onClick={() => setSignatureDoc(doc)}
                            data-testid={`send-signature-${doc.document_id}`}
                            title="Send for signature">
                            <PenLine className="w-4 h-4 text-purple-500" />
                          </Button>
                        )}
                        {(doc.status === "approved" || doc.status === "published") && (
                          <Button variant="ghost" size="icon" onClick={() => reopenDoc(doc)}
                            data-testid={`reopen-${doc.document_id}`} title="Reopen for edits">
                            <Edit2 className="w-4 h-4 text-orange-500" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => viewHistory(doc)}
                          data-testid={`history-${doc.document_id}`} title="Workflow history">
                          <History className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => downloadPdf(doc)}
                          data-testid={`pdf-${doc.document_id}`} title="Download PDF">
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setViewingDoc(doc)}
                          data-testid={`view-doc-${doc.document_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditingDoc(doc)}
                          data-testid={`edit-doc-${doc.document_id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`delete-doc-${doc.document_id}`}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>{t("docs.delete_title")}</AlertDialogTitle>
                              <AlertDialogDescription>
                                {t("docs.delete_desc")}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDeleteDocument(doc.document_id)}>
                                {t("delete")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
              );
            })}
          </AnimatePresence>

          {filteredDocuments.length === 0 && (
            <EmptyState
              icon={FileText}
              title={t("docs.no_docs_title")}
              description={t("docs.no_docs_desc")}
              action={
                <Button onClick={() => setShowAddDoc(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t("docs.create_first")}
                </Button>
              }
            />
          )}
        </div>
      </ScrollArea>

      {/* Add Document Sheet */}
      <Sheet open={showAddDoc} onOpenChange={setShowAddDoc}>
        <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <SheetTitle>{t("docs.create_title")}</SheetTitle>
            <SheetDescription>{t("docs.create_desc")}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="doc-title">{t("docs.title_label")} *</Label>
              <Input
                id="doc-title"
                value={newDoc.title}
                onChange={(e) => setNewDoc({ ...newDoc, title: e.target.value })}
                placeholder={t("docs.ph_title")}
                data-testid="doc-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("docs.type_label")}</Label>
                <Select
                  value={newDoc.document_type}
                  onValueChange={(v) => setNewDoc({ ...newDoc, document_type: v })}
                >
                  <SelectTrigger data-testid="doc-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{t(type.labelKey)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("docs.framework_label")}</Label>
                <Select
                  value={newDoc.framework}
                  onValueChange={(v) => setNewDoc({ ...newDoc, framework: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("docs.framework_label")} />
                  </SelectTrigger>
                  <SelectContent>
                    {frameworks.map(fw => (
                      <SelectItem key={fw.value} value={fw.value}>{fw.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="doc-folder">{t("docs.folder_label")}</Label>
              <Input
                id="doc-folder"
                value={newDoc.folder}
                onChange={(e) => setNewDoc({ ...newDoc, folder: e.target.value })}
                placeholder={t("docs.ph_folder")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="doc-content">{t("docs.content_label")}</Label>
              <Textarea
                id="doc-content"
                value={newDoc.content}
                onChange={(e) => setNewDoc({ ...newDoc, content: e.target.value })}
                placeholder={t("docs.ph_content")}
                className="min-h-[200px] font-mono text-sm"
                data-testid="doc-content-input"
              />
            </div>
          </div>
          </div>
          <SheetFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setShowAddDoc(false)}>{t("cancel")}</Button>
            <Button onClick={handleAddDocument} data-testid="save-doc-btn">
              <Save className="w-4 h-4 mr-2" />
              {t("docs.create_btn")}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Edit Document Sheet */}
      <Sheet open={!!editingDoc} onOpenChange={() => setEditingDoc(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <SheetTitle>{t("edit")} {t("docs.title_label")}</SheetTitle>
          </SheetHeader>
          {editingDoc && (
            <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t("docs.title_label")}</Label>
                <Input
                  value={editingDoc.title}
                  onChange={(e) => setEditingDoc({ ...editingDoc, title: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("docs.type_label")}</Label>
                  <Select
                    value={editingDoc.document_type}
                    onValueChange={(v) => setEditingDoc({ ...editingDoc, document_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {documentTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{t(type.labelKey)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("status")}</Label>
                  <Select
                    value={editingDoc.status}
                    onValueChange={(v) => handleUpdateStatus(editingDoc.document_id, v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map(s => (
                        <SelectItem key={s.value} value={s.value}>{t(s.labelKey)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("docs.content_label")}</Label>
                <Textarea
                  value={editingDoc.content || ""}
                  onChange={(e) => setEditingDoc({ ...editingDoc, content: e.target.value })}
                  className="min-h-[200px] font-mono text-sm"
                />
              </div>
            </div>
          </div>
          )}
          <SheetFooter className="px-6 py-4 border-t shrink-0">
            <Button variant="outline" onClick={() => setEditingDoc(null)}>{t("cancel")}</Button>
            <Button onClick={handleUpdateDocument}>
              <Save className="w-4 h-4 mr-2" />
              {t("save")}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* View Document Dialog */}
      <Dialog open={!!viewingDoc} onOpenChange={() => setViewingDoc(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {viewingDoc && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="inline-flex items-center gap-1.5">
                    {ViewingDocIcon && <ViewingDocIcon className={`w-3.5 h-3.5 ${TEXT_TONE[getStatusTone("doc", viewingDoc.status)]}`} />}
                    <StatusBadge kind="doc" value={viewingDoc.status} />
                  </span>
                  <Badge variant="secondary" className="capitalize">
                    {viewingDoc.document_type}
                  </Badge>
                  {viewingDoc.framework && (
                    <Badge variant="outline" className={getFrameworkBadgeClass(viewingDoc.framework)}>
                      {frameworks.find(f => f.value === viewingDoc.framework)?.label}
                    </Badge>
                  )}
                  <span className="text-sm text-muted-foreground">Version {viewingDoc.version}</span>
                </div>
                <DialogTitle className="text-xl">{viewingDoc.title}</DialogTitle>
                <DialogDescription>
                  Last updated: {new Date(viewingDoc.updated_at).toLocaleString()}
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4">
                <div className="p-4 rounded-lg bg-muted/50 min-h-[300px]">
                  <pre className="whitespace-pre-wrap font-mono text-sm">
                    {viewingDoc.content || "No content yet."}
                  </pre>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Workflow History Dialog */}
      <Dialog open={!!historyDoc} onOpenChange={() => { setHistoryDoc(null); setHistoryData(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {historyDoc && (
            <>
              <DialogHeader>
                <DialogTitle>Workflow History</DialogTitle>
                <DialogDescription>{historyDoc.title}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-4">
                {!historyData ? (
                  <p className="text-sm text-muted-foreground">Loading...</p>
                ) : !historyData.workflow_history || historyData.workflow_history.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic py-6 text-center">
                    No workflow history yet. Status changes will be recorded here.
                  </p>
                ) : (
                  <div className="relative space-y-4">
                    {historyData.workflow_history.slice().reverse().map((entry, idx) => (
                      <div key={idx} className="flex gap-3 relative">
                        <div className="flex flex-col items-center">
                          <div className="w-2 h-2 rounded-full bg-primary mt-1.5" />
                          {idx < historyData.workflow_history.length - 1 && (
                            <div className="w-px flex-1 bg-border mt-1" />
                          )}
                        </div>
                        <div className="flex-1 pb-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-[10px]">
                              {entry.from_status || "—"} → <b>{entry.to_status}</b>
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(entry.timestamp).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            by {entry.changed_by_email || entry.changed_by}
                          </p>
                          {entry.comment && (
                            <p className="text-sm mt-1.5 italic">"{entry.comment}"</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Send for Signature Dialog */}
      <SendForSignatureDialog
        open={!!signatureDoc}
        document={signatureDoc}
        onClose={() => setSignatureDoc(null)}
        onSent={() => { setSignatureDoc(null); fetchDocuments(); }}
      />
    </div>
  );
};

export default Documents;
